import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    Camera, Upload, Loader2, ShieldAlert, Link2, QrCode, FileText, AlertTriangle,
    Download, Share2, ChevronRight, History, Eye, Save, Network, X,
} from 'lucide-react';
import {
    caseService,
    instagramOsintService,
    type InstagramOsintRunResult,
    type InstagramArtifact,
} from '../services/api';
import { openOsintReport } from '../utils/osintReport';
import { INSTAGRAM_PUSH_KEY, instagramWorkbenchUrl } from '../utils/workbenchCaseLink';
import { InstagramOsintGuide } from '../components/instagram/InstagramOsintGuide';
import { InstagramFlowCoach, type FlowStepId } from '../components/instagram/InstagramFlowCoach';
import { InstagramWelcome } from '../components/instagram/InstagramWelcome';
import {
    ARTIFACT_LABELS,
    SURFACE_LABELS,
    SURFACE_STYLES,
    groupArtifacts,
} from '../components/instagram/artifactLabels';
import { labelSeedSources } from '../components/instagram/seedSourceLabels';
import { FileDropzone } from '../components/ui/FileDropzone';

interface CaseLite { id: string; title?: string; fir_number?: string; }

interface RunLite {
    id: string;
    suspect_handle?: string;
    source?: string;
    status?: string;
    media_count?: number;
    artifact_count?: number;
    created_at?: string;
    committed?: boolean;
}

type MaterialTab = 'bio' | 'upload' | 'fetch';
type SaveMode = 'preview' | 'save';
type IdentityPath = 'handle' | 'seed';
type SeedLookupStatus = 'idle' | 'loading' | 'found' | 'not_found' | 'not_tested' | 'error';

function errorToMessage(e: any, fallback: string): string {
    const detail = e?.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
        const msgs = detail
            .map((d: any) => {
                if (typeof d === 'string') return d;
                const where = Array.isArray(d?.loc) ? d.loc.filter((x: any) => x !== 'body').join('.') : '';
                return d?.msg ? (where ? `${d.msg} (${where})` : d.msg) : '';
            })
            .filter(Boolean);
        if (msgs.length) return msgs.join('; ');
    }
    if (detail && typeof detail === 'object') return JSON.stringify(detail);
    return e?.message || fallback;
}

function formatWhen(iso?: string): string {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso;
    }
}

const InstagramOSINT: React.FC = () => {
    const [cases, setCases] = useState<CaseLite[]>([]);
    const [caseId, setCaseId] = useState('');
    const [handle, setHandle] = useState('');
    const [seed, setSeed] = useState('');
    const [seedLoading, setSeedLoading] = useState(false);
    const [seedMsg, setSeedMsg] = useState('');
    const [seedStatus, setSeedStatus] = useState<SeedLookupStatus>('idle');
    const [identityPath, setIdentityPath] = useState<IdentityPath>('handle');
    const [seedCandidates, setSeedCandidates] = useState<Array<{ handle: string; sources: string[]; evidence: string[] }>>([]);
    const [files, setFiles] = useState<File[]>([]);
    const [caption, setCaption] = useState('');
    const [sourceUrl, setSourceUrl] = useState('');
    const [bio, setBio] = useState('');
    const [fullName, setFullName] = useState('');
    const [externalUrl, setExternalUrl] = useState('');
    const [publicEmail, setPublicEmail] = useState('');
    const [publicPhone, setPublicPhone] = useState('');
    const [saveMode, setSaveMode] = useState<SaveMode>('preview');
    const [materialTab, setMaterialTab] = useState<MaterialTab>('bio');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState<InstagramOsintRunResult | null>(null);
    const [backends, setBackends] = useState<string[]>([]);
    const [fetchLimit, setFetchLimit] = useState(12);
    const [legalBasis, setLegalBasis] = useState('');
    const [pushing, setPushing] = useState(false);
    const [acting, setActing] = useState(false);
    const [runs, setRuns] = useState<RunLite[]>([]);
    const [runsLoading, setRunsLoading] = useState(false);
    const [statusMsg, setStatusMsg] = useState('');
    const navigate = useNavigate();
    const step1Ref = useRef<HTMLElement | null>(null);
    const step2Ref = useRef<HTMLElement | null>(null);
    const step3Ref = useRef<HTMLElement | null>(null);
    const step4Ref = useRef<HTMLElement | null>(null);
    const runAbortRef = useRef<AbortController | null>(null);
    const seedAbortRef = useRef<AbortController | null>(null);

    const isAbortError = (e: unknown): boolean => {
        const err = e as { code?: string; name?: string };
        return err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError';
    };

    const cancelActiveRun = () => {
        runAbortRef.current?.abort();
        runAbortRef.current = null;
        setLoading(false);
        setActing(false);
        setStatusMsg('Cancelled.');
    };

    const cancelSeedLookup = () => {
        seedAbortRef.current?.abort();
        seedAbortRef.current = null;
        setSeedLoading(false);
        setSeedStatus('idle');
        setSeedMsg('Search cancelled.');
    };

    useEffect(() => () => {
        runAbortRef.current?.abort();
        seedAbortRef.current?.abort();
    }, []);

    const jumpToStep = (id: FlowStepId) => {
        const map: Record<FlowStepId, React.RefObject<HTMLElement | null>> = {
            1: step1Ref,
            2: step2Ref,
            3: step3Ref,
            4: step4Ref,
        };
        map[id].current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const loadRuns = useCallback(async (cid: string) => {
        if (!cid) { setRuns([]); return; }
        setRunsLoading(true);
        try {
            const list = await instagramOsintService.listRuns(cid);
            const rows = Array.isArray(list) ? list : (list?.runs || list?.data || []);
            setRuns(rows.map((r: any) => ({
                id: String(r.id),
                suspect_handle: r.suspect_handle,
                source: r.source,
                status: r.status,
                media_count: r.media_count,
                artifact_count: r.artifact_count,
                created_at: r.created_at,
            })));
        } catch {
            setRuns([]);
        } finally {
            setRunsLoading(false);
        }
    }, []);

    useEffect(() => {
        caseService.getAll().then((d: any) => {
            const list = Array.isArray(d) ? d : (d?.cases || d?.data || []);
            setCases(list);
            if (list.length && !caseId) setCaseId(list[0].id);
        }).catch(() => setCases([]));
        instagramOsintService.backends().then((b) => setBackends(b.backends || [])).catch(() => setBackends([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const applyCandidate = (h: string) => {
        setHandle(h.replace(/^@/, ''));
        setIdentityPath('handle');
        jumpToStep(2);
    };

    const resolveSeed = async () => {
        if (!caseId || !seed.trim()) {
            setSeedStatus('error');
            setSeedMsg('Select a case, then enter a phone, email, or UPI ID.');
            return;
        }
        setSeedLoading(true);
        setSeedStatus('loading');
        setSeedMsg(`Searching this case, P2P ads, and MuleHunter for “${seed.trim()}” — not Instagram.com.`);
        setSeedCandidates([]);
        setError('');
        seedAbortRef.current?.abort();
        const ac = new AbortController();
        seedAbortRef.current = ac;
        try {
            const res = await instagramOsintService.resolveSeed(caseId, seed.trim(), undefined, { signal: ac.signal });
            setSeedCandidates(res.candidates || []);
            const st = res.status;
            setSeedStatus(
                st === 'found' ? 'found'
                    : st === 'not_tested' ? 'not_tested'
                        : 'not_found',
            );
            if (st === 'found' && res.candidates?.length === 1) {
                setSeedMsg(res.message || 'One candidate. Click it to fill the handle, then paste bio in Step 2.');
            } else if (st === 'found') {
                setSeedMsg(res.message || 'Click a candidate to fill the @handle, then continue to Step 2.');
            } else if (st === 'not_tested') {
                setSeedMsg(res.message || 'Database unavailable. Paste a public @handle or bio instead.');
            } else {
                setSeedMsg(
                    res.message
                    || 'No handle in licensed records for that seed. Paste bio or screenshots in Step 2 (preview works without a handle). Live fetch still needs an @handle.',
                );
            }
        } catch (e: any) {
            if (isAbortError(e)) return;
            setSeedStatus('error');
            setSeedMsg(errorToMessage(e, 'Could not search records'));
        } finally {
            if (seedAbortRef.current === ac) seedAbortRef.current = null;
            setSeedLoading(false);
        }
    };

    useEffect(() => {
        if (caseId) loadRuns(caseId);
    }, [caseId, loadRuns]);

    const counts = result?.counts || {};
    const hasProfileData = !!(bio.trim() || fullName.trim() || externalUrl.trim() || publicEmail.trim() || publicPhone.trim());
    const commit = saveMode === 'save';
    const canPersistWithoutHandle = !!seed.trim() && !handle.trim();
    const canSubmit =
        !!caseId
        && (files.length > 0 || hasProfileData || (materialTab === 'fetch' && handle.trim() && legalBasis.trim() && backends.length > 0))
        && !loading
        && (!commit || !!handle.trim() || canPersistWithoutHandle);

    const submitDisabledReason = useMemo(() => {
        if (!caseId) return 'Select a case first';
        if (commit && !handle.trim() && !seed.trim()) {
            return 'Add the @handle, or a phone/UPI/email seed, to save to the case';
        }
        if (materialTab === 'fetch') {
            if (!backends.length) return 'Live fetch is not configured on this server';
            if (!handle.trim()) return 'Enter the Instagram handle';
            if (!legalBasis.trim()) return 'Enter the legal basis / authorising order';
            return '';
        }
        if (!files.length && !hasProfileData) return 'Paste a bio or upload screenshots';
        return '';
    }, [caseId, commit, handle, seed, materialTab, backends.length, legalBasis, files.length, hasProfileData]);

    const applyResult = (res: InstagramOsintRunResult) => {
        setResult(res);
        if (res.committed) loadRuns(caseId);
    };

    const runSupplied = async (forceCommit?: boolean) => {
        const doCommit = forceCommit ?? commit;
        if (!caseId || (!files.length && !hasProfileData)) return;
        if (doCommit && !handle.trim() && !seed.trim()) {
            setError('Add the Instagram @handle or a phone/UPI/email seed to save to the case.');
            return;
        }
        setLoading(true); setError(''); setStatusMsg(''); setResult(null);
        runAbortRef.current?.abort();
        const ac = new AbortController();
        runAbortRef.current = ac;
        try {
            const res = await instagramOsintService.suppliedMedia(caseId, handle.trim() || '', files, {
                captions: files.map((_, i) => (i === 0 ? caption : undefined)),
                sourceUrls: files.map((_, i) => (i === 0 ? sourceUrl : undefined)),
                bio: bio.trim() || undefined,
                fullName: fullName.trim() || undefined,
                externalUrl: externalUrl.trim() || undefined,
                publicEmail: publicEmail.trim() || undefined,
                publicPhone: publicPhone.trim() || undefined,
                seed: seed.trim() || undefined,
                commit: doCommit,
                signal: ac.signal,
            });
            applyResult(res);
            const underSeed = res.warnings?.includes('persisted_under_seed_key');
            setStatusMsg(
                doCommit
                    ? (underSeed
                        ? 'Saved to case under seed key (no @handle yet). Review leads below.'
                        : 'Saved to case. Review leads below.')
                    : 'Preview ready — not saved yet.',
            );
        } catch (e: any) {
            if (isAbortError(e)) return;
            setError(errorToMessage(e, 'Could not find leads'));
        } finally {
            if (runAbortRef.current === ac) runAbortRef.current = null;
            setLoading(false);
        }
    };

    const runFetch = async (forceCommit?: boolean) => {
        const doCommit = forceCommit ?? commit;
        if (!caseId || !handle.trim() || !legalBasis.trim()) return;
        setLoading(true); setError(''); setStatusMsg(''); setResult(null);
        runAbortRef.current?.abort();
        const ac = new AbortController();
        runAbortRef.current = ac;
        try {
            const res = await instagramOsintService.fetchFromHandle(caseId, handle.trim(), {
                limit: fetchLimit,
                commit: doCommit,
                legalBasis: legalBasis.trim(),
                signal: ac.signal,
            });
            applyResult(res);
            setStatusMsg(doCommit ? 'Saved to case. Review leads below.' : 'Preview ready — not saved yet.');
        } catch (e: any) {
            if (isAbortError(e)) return;
            setError(errorToMessage(e, 'Could not pull posts'));
        } finally {
            if (runAbortRef.current === ac) runAbortRef.current = null;
            setLoading(false);
        }
    };

    const findLeads = () => {
        if (materialTab === 'fetch') return runFetch();
        return runSupplied();
    };

    const pushToCanvas = async (runId?: string) => {
        const id = runId || result?.run_id;
        if (!id || !caseId) return;
        setPushing(true); setError(''); setStatusMsg('');
        try {
            const ge = await instagramOsintService.graphElements(caseId, id);
            if (!ge.nodes.length) {
                setError('No usable leads to open on Workbench yet.');
                return;
            }
            const edges = ge.edges.map((e) => ({
                data: { ...e.data, edgeType: e.data.edge_type === 'osint' ? 'osint-link' : undefined },
            }));
            const elements = [...ge.nodes, ...edges];
            sessionStorage.setItem(INSTAGRAM_PUSH_KEY, JSON.stringify({
                savedAt: Date.now(), caseId, elements,
            }));
            navigate(instagramWorkbenchUrl(caseId));
        } catch (e: any) {
            setError(errorToMessage(e, 'Could not open Workbench'));
        } finally {
            setPushing(false);
        }
    };

    /** Save (if needed) + promote fusion graph + open Workbench. */
    const saveAndInvestigate = async () => {
        if (!caseId || (!handle.trim() && !seed.trim())) {
            setError('Case plus @handle or phone/UPI/email seed are required to save and investigate.');
            return;
        }
        setActing(true); setError(''); setStatusMsg('');
        runAbortRef.current?.abort();
        const ac = new AbortController();
        runAbortRef.current = ac;
        try {
            let runId = result?.run_id || null;
            let latest = result;
            if (!result?.committed || !runId) {
                if (materialTab === 'fetch') {
                    if (!handle.trim()) {
                        setError('Live fetch still needs an Instagram @handle.');
                        if (runAbortRef.current === ac) runAbortRef.current = null;
                        setActing(false);
                        return;
                    }
                    latest = await instagramOsintService.fetchFromHandle(caseId, handle.trim(), {
                        limit: fetchLimit,
                        commit: true,
                        legalBasis: legalBasis.trim() || 'Officer save & investigate action',
                        signal: ac.signal,
                    });
                } else {
                    if (!files.length && !hasProfileData) {
                        setError('Add bio or screenshots before saving.');
                        if (runAbortRef.current === ac) runAbortRef.current = null;
                        setActing(false);
                        return;
                    }
                    latest = await instagramOsintService.suppliedMedia(caseId, handle.trim() || '', files, {
                        captions: files.map((_, i) => (i === 0 ? caption : undefined)),
                        sourceUrls: files.map((_, i) => (i === 0 ? sourceUrl : undefined)),
                        bio: bio.trim() || undefined,
                        fullName: fullName.trim() || undefined,
                        externalUrl: externalUrl.trim() || undefined,
                        publicEmail: publicEmail.trim() || undefined,
                        publicPhone: publicPhone.trim() || undefined,
                        seed: seed.trim() || undefined,
                        commit: true,
                        signal: ac.signal,
                    });
                }
                applyResult(latest);
                runId = latest.run_id;
            }
            if (!runId) {
                setError('Save finished but no run id was returned.');
                return;
            }
            try {
                await instagramOsintService.promoteToGraph(caseId);
            } catch {
                /* promote is best-effort; still try canvas */
            }
            setStatusMsg('Saved to case and case graph. Opening Workbench…');
            await pushToCanvas(runId);
        } catch (e: any) {
            if (isAbortError(e)) return;
            setError(errorToMessage(e, 'Save & investigate failed'));
        } finally {
            if (runAbortRef.current === ac) runAbortRef.current = null;
            setActing(false);
        }
    };

    const reopenRun = async (runId: string) => {
        if (!caseId) return;
        setLoading(true); setError(''); setStatusMsg('');
        try {
            const arts = await instagramOsintService.runArtifacts(caseId, runId);
            const runMeta = runs.find((r) => r.id === runId);
            const synthetic: InstagramOsintRunResult = {
                run_id: runId,
                case_id: caseId,
                suspect_handle: runMeta?.suspect_handle || handle || '',
                committed: true,
                media: [],
                artifacts: arts as InstagramArtifact[],
                counts: {
                    total: arts.length,
                    valid: arts.filter((a: any) => a.valid).length,
                    rejected: arts.filter((a: any) => !a.valid).length,
                },
                by_surface: {},
                manifest_ndjson: null,
                manifest_signature: null,
                warnings: ['Reopened from history — media/OCR detail not reloaded.'],
            };
            for (const a of arts as InstagramArtifact[]) {
                synthetic.counts[a.artifact_type] = (synthetic.counts[a.artifact_type] || 0) + 1;
                const s = a.source_surface || 'post';
                synthetic.by_surface[s] = (synthetic.by_surface[s] || 0) + 1;
            }
            setResult(synthetic);
            if (runMeta?.suspect_handle) setHandle(runMeta.suspect_handle.replace(/^@/, ''));
            setStatusMsg('Loaded past extract. Use next steps below.');
        } catch (e: any) {
            setError(errorToMessage(e, 'Could not reopen run'));
        } finally {
            setLoading(false);
        }
    };

    const downloadManifest = () => {
        if (!result?.manifest_ndjson) return;
        const blob = new Blob([result.manifest_ndjson], { type: 'application/x-ndjson' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `instagram_osint_${(result.suspect_handle || 'preview').replace(/[^a-z0-9]/gi, '_')}.manifest.ndjson`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const step1Done = !!caseId;
    const step2Done = hasProfileData || files.length > 0 || (materialTab === 'fetch' && !!handle.trim());
    const step3Done = !!result;
    const step4Done = !!result?.committed;

    const identityOpen = !handle.trim() && seedStatus !== 'not_found';
    const activeStep: FlowStepId = !step1Done || (identityOpen && !step2Done)
        ? 1
        : !step2Done ? 2 : !step3Done ? 3 : 4;

    const nextMessage = useMemo(() => {
        if (!caseId) {
            return 'Choose the investigation case. Saves attach to the same case as Workbench traces.';
        }
        if (seedStatus === 'not_found' && !handle.trim()) {
            return 'No @handle in licensed records. Go to Step 2: paste bio or screenshots anyway (preview works). Save can use this seed until a handle is known.';
        }
        if (seedStatus === 'found' && seedCandidates.length && !handle.trim()) {
            return 'Click a candidate below to fill the @handle, then paste bio in Step 2.';
        }
        if (!handle.trim()) {
            if (identityPath === 'seed') {
                return 'Enter phone, email, or UPI, then Search case records. That does not query Instagram.com.';
            }
            if (saveMode === 'save') {
                return 'Paste the @handle, or switch to phone/email/UPI and search case records.';
            }
            return 'Paste the @handle, or choose phone/email/UPI to search records we already hold. Preview works without a handle.';
        }
        if (!step2Done) {
            return 'Step 2: Paste the profile bio (recommended) or upload payment screenshots.';
        }
        if (!result && !loading) {
            return 'Step 3: Click Find leads for wallets, UPI IDs, and phones from the text or images.';
        }
        if (result && !result.committed) {
            return 'Review green Usable rows, then Save to case & open Workbench to triage wallets on the graph.';
        }
        if (result?.committed) {
            return 'Leads are on the case. Trace on Workbench. After two+ handles on this case, use Compare IG accounts.';
        }
        return 'Follow the numbered steps. Do now always says the next action.';
    }, [caseId, handle, saveMode, step2Done, result, loading, seedStatus, seedCandidates.length, identityPath]);

    const flowSteps = useMemo(
        () => [
            {
                id: 1 as FlowStepId,
                label: 'Case',
                short: 'Case + who (handle or phone/UPI)',
                done: step1Done,
                active: activeStep === 1,
            },
            {
                id: 2 as FlowStepId,
                label: 'Material',
                short: 'Bio or screenshots',
                done: step2Done,
                active: activeStep === 2,
            },
            {
                id: 3 as FlowStepId,
                label: 'Find leads',
                short: 'Run extraction',
                done: step3Done,
                active: activeStep === 3,
            },
            {
                id: 4 as FlowStepId,
                label: 'Save & act',
                short: 'Case → Workbench',
                done: step4Done,
                active: activeStep === 4,
            },
        ],
        [step1Done, step2Done, step3Done, step4Done, activeStep],
    );

    const stepRing = (id: FlowStepId) =>
        activeStep === id
            ? 'ring-2 ring-pink-500/50 border-pink-500/40'
            : 'border-border';

    return (
        <div className="p-6 max-w-5xl mx-auto space-y-5" data-tour="instagram-osint-page">
            {/* Header */}
            <header className="space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                    <Camera className="w-6 h-6 text-pink-500" />
                    <h1 className="text-xl font-semibold">Instagram leads</h1>
                    <span className="text-xs text-muted-foreground">OSINT for case file</span>
                    <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30"
                        title="Tier-4 OSINT: corroborate on-chain or bank before relying"
                    >
                        Lead — check before relying
                    </span>
                    <Link
                        to="/instagram-network"
                        className="ml-auto text-xs inline-flex items-center gap-1 text-pink-600 hover:underline"
                    >
                        <Network className="w-3.5 h-3.5" /> Compare IG accounts
                    </Link>
                </div>
                <p className="text-sm text-muted-foreground max-w-3xl">
                    Extract wallets, UPI, and phones from the suspect&apos;s own bio or screenshots.
                    Save writes to the <strong className="text-foreground font-medium">same case</strong> as Workbench (nodes on the graph).
                    Handle search uses this case, P2P ads, and MuleHunter — not Instagram login.
                    Compare IG accounts is for after two+ handles on that case share a wallet or UPI.
                </p>
                <p className="text-xs text-muted-foreground/80 flex items-center gap-1">
                    <ShieldAlert className="w-3.5 h-3.5 shrink-0" />
                    Named suspect&apos;s own public content only — no followers, likers, or mass monitoring.
                </p>
            </header>

            <InstagramWelcome />
            <InstagramFlowCoach steps={flowSteps} nextMessage={nextMessage} onJump={jumpToStep} />
            <InstagramOsintGuide />

            {/* Step 1 — Case */}
            <section
                ref={step1Ref}
                className={`bg-card border rounded-lg p-4 space-y-3 ${stepRing(1)}`}
                data-tour="instagram-step-case"
            >
                <div className="flex items-center gap-2 text-sm font-medium">
                    <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-semibold">1</span>
                    Case and who this is
                    {activeStep === 1 && (
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground ml-1">start here</span>
                    )}
                </div>
                <p className="text-xs text-muted-foreground">
                    Same case as Workbench traces. Then say how you know the account — @handle, or a phone/email/UPI we may already have on file.
                </p>
                <select
                    className="w-full max-w-md bg-background border border-border rounded-md px-3 py-2 text-sm"
                    value={caseId}
                    onChange={(e) => setCaseId(e.target.value)}
                    data-tour="instagram-case-select"
                >
                    <option value="">Select a case…</option>
                    {cases.map((c) => (
                        <option key={c.id} value={c.id}>{c.title || c.fir_number || c.id}</option>
                    ))}
                </select>

                <fieldset className="space-y-3 max-w-xl">
                    <legend className="text-sm font-medium mb-1">How do you identify the account?</legend>
                    <label className="flex items-start gap-2 text-sm cursor-pointer">
                        <input
                            type="radio"
                            name="ig-identity-path"
                            className="mt-1"
                            checked={identityPath === 'handle'}
                            onChange={() => setIdentityPath('handle')}
                        />
                        <span>
                            <span className="text-foreground">I have the @handle</span>
                            <span className="block text-xs text-muted-foreground">Required to save onto Workbench. Preview can skip this.</span>
                        </span>
                    </label>
                    {identityPath === 'handle' && (
                        <label className="block text-sm space-y-1 pl-6">
                            <span className="sr-only">Suspect Instagram handle</span>
                            <input
                                className="w-full max-w-md bg-background border border-border rounded-md px-3 py-2 text-sm"
                                placeholder="@suspect_handle"
                                value={handle}
                                onChange={(e) => setHandle(e.target.value)}
                                data-tour="instagram-handle-input"
                            />
                        </label>
                    )}
                    <label className="flex items-start gap-2 text-sm cursor-pointer">
                        <input
                            type="radio"
                            name="ig-identity-path"
                            className="mt-1"
                            checked={identityPath === 'seed'}
                            onChange={() => setIdentityPath('seed')}
                        />
                        <span>
                            <span className="text-foreground">I only have phone, email, or UPI</span>
                            <span className="block text-xs text-muted-foreground">Searches this case, P2P ads, and MuleHunter. Does not search Instagram.com.</span>
                        </span>
                    </label>
                    {identityPath === 'seed' && (
                        <div className="pl-6 space-y-2">
                            <div className="flex gap-2 max-w-md">
                                <input
                                    className="flex-1 bg-background border border-border rounded-md px-3 py-2 text-sm"
                                    placeholder="+91 98765 43210 or name@okaxis"
                                    value={seed}
                                    onChange={(e) => setSeed(e.target.value)}
                                    data-tour="instagram-seed-input"
                                    disabled={!caseId}
                                />
                                <button
                                    type="button"
                                    className="px-3 py-2 text-sm border border-border rounded-md disabled:opacity-50 whitespace-nowrap"
                                    disabled={seedLoading || !caseId || !seed.trim()}
                                    onClick={resolveSeed}
                                    data-tour="instagram-resolve-seed"
                                    title={!caseId ? 'Select a case first' : undefined}
                                >
                                    {seedLoading ? 'Searching records…' : 'Search case records'}
                                </button>
                                {seedLoading && (
                                    <button
                                        type="button"
                                        onClick={cancelSeedLookup}
                                        className="px-3 py-2 text-sm border border-border rounded-md hover:bg-accent inline-flex items-center gap-1"
                                        title="Cancel seed lookup"
                                    >
                                        <X className="w-3.5 h-3.5" /> Cancel
                                    </button>
                                )}
                            </div>
                            {!caseId && (
                                <p className="text-xs text-muted-foreground">Select a case first — lookup is scoped to that file.</p>
                            )}
                            {seedMsg && (
                                <p
                                    className={`text-xs ${seedStatus === 'not_found' || seedStatus === 'error' ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground'}`}
                                    data-testid="instagram-seed-status"
                                >
                                    {seedMsg}
                                </p>
                            )}
                            {seedCandidates.length > 0 && (
                                <ul className="text-sm space-y-2" data-testid="instagram-seed-candidates">
                                    {seedCandidates.map((c) => (
                                        <li key={c.handle}>
                                            <button
                                                type="button"
                                                className="text-left w-full max-w-md rounded-md border border-border px-3 py-2 hover:bg-secondary/50"
                                                onClick={() => applyCandidate(c.handle)}
                                            >
                                                <span className="font-medium text-primary">@{c.handle}</span>
                                                <span className="block text-xs text-muted-foreground mt-0.5">
                                                    {labelSeedSources(c.sources)} — click to fill handle, then paste bio (Step 2)
                                                </span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                    {identityPath === 'handle' && handle.trim() && (
                        <p className="text-xs text-muted-foreground pl-6">
                            For multiple seller accounts, save one handle at a time, then Compare IG accounts for shared wallets.
                        </p>
                    )}
                </fieldset>
            </section>

            {/* Step 2 — Material */}
            <section
                ref={step2Ref}
                className={`bg-card border rounded-lg p-4 space-y-3 ${stepRing(2)}`}
                data-tour="instagram-step-material"
            >
                <div className="flex items-center gap-2 text-sm font-medium">
                    <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-semibold">2</span>
                    What do you have from Instagram?
                    {activeStep === 2 && (
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground ml-1">do this next</span>
                    )}
                </div>
                <div className="flex flex-wrap gap-2" role="tablist">
                    {(
                        [
                            { id: 'bio' as const, label: 'Paste bio & links', hint: 'Recommended first' },
                            { id: 'upload' as const, label: 'Upload screenshots', hint: 'Photos / reels' },
                            ...(backends.length > 0
                                ? [{ id: 'fetch' as const, label: 'Pull public posts', hint: 'Advanced' }]
                                : []),
                        ]
                    ).map((t) => (
                        <button
                            key={t.id}
                            type="button"
                            role="tab"
                            aria-selected={materialTab === t.id}
                            onClick={() => setMaterialTab(t.id)}
                            className={`px-3 py-1.5 rounded-md text-xs border transition-colors ${
                                materialTab === t.id
                                    ? 'bg-primary text-primary-foreground border-primary'
                                    : 'bg-background border-border text-muted-foreground hover:bg-secondary'
                            }`}
                        >
                            {t.label}
                            <span className="opacity-70 ml-1">· {t.hint}</span>
                        </button>
                    ))}
                </div>

                {materialTab === 'bio' && (
                    <div className="space-y-3 pt-1">
                        <p className="text-xs text-muted-foreground">
                            The bio and its link are usually the richest source of wallets and payment IDs. Works with no images.
                        </p>
                        <textarea
                            className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm min-h-[72px]"
                            placeholder="Paste bio text (e.g. USDT rates, pay T… or name@okhdfcbank, DM for rates)"
                            value={bio}
                            onChange={(e) => setBio(e.target.value)}
                            data-tour="instagram-bio-input"
                        />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <input
                                className="bg-background border border-border rounded-md px-3 py-2 text-sm"
                                placeholder="Bio link (Linktree, t.me, wa.me)"
                                value={externalUrl}
                                onChange={(e) => setExternalUrl(e.target.value)}
                            />
                            <input
                                className="bg-background border border-border rounded-md px-3 py-2 text-sm"
                                placeholder="Display name (optional)"
                                value={fullName}
                                onChange={(e) => setFullName(e.target.value)}
                            />
                            <input
                                className="bg-background border border-border rounded-md px-3 py-2 text-sm"
                                placeholder="Public contact email"
                                value={publicEmail}
                                onChange={(e) => setPublicEmail(e.target.value)}
                            />
                            <input
                                className="bg-background border border-border rounded-md px-3 py-2 text-sm"
                                placeholder="Public contact phone"
                                value={publicPhone}
                                onChange={(e) => setPublicPhone(e.target.value)}
                            />
                        </div>
                    </div>
                )}

                {materialTab === 'upload' && (
                    <div className="space-y-3 pt-1">
                        <p className="text-xs text-muted-foreground">
                            Screenshots of posts, reels, or payment QRs. We read text and QR codes for you.
                        </p>
                        <FileDropzone
                            bare
                            compact
                            multiple
                            accept="image/*,video/*"
                            onFilesChange={(next) => setFiles(next)}
                            title="Screenshots / media"
                            description="JPG · PNG · GIF · video"
                            data-tour="instagram-file-input"
                        />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <input
                                className="bg-background border border-border rounded-md px-3 py-2 text-sm"
                                placeholder="Caption (optional, first file)"
                                value={caption}
                                onChange={(e) => setCaption(e.target.value)}
                            />
                            <input
                                className="bg-background border border-border rounded-md px-3 py-2 text-sm"
                                placeholder="Post link (optional)"
                                value={sourceUrl}
                                onChange={(e) => setSourceUrl(e.target.value)}
                            />
                        </div>
                        {/* Allow bio fields while on upload tab too — collapsed helper */}
                        <details className="text-xs">
                            <summary className="cursor-pointer text-muted-foreground">Also paste bio here (optional)</summary>
                            <textarea
                                className="mt-2 w-full bg-background border border-border rounded-md px-3 py-2 text-sm min-h-[56px]"
                                placeholder="Bio text"
                                value={bio}
                                onChange={(e) => setBio(e.target.value)}
                            />
                        </details>
                    </div>
                )}

                {materialTab === 'fetch' && backends.length > 0 && (
                    <div className="space-y-3 pt-1 border border-amber-500/20 rounded-md p-3 bg-amber-500/5">
                        <div className="flex items-center gap-2 text-sm font-medium">
                            <Link2 className="w-4 h-4" /> Advanced: pull public posts by handle
                        </div>
                        <p className="text-xs text-muted-foreground">
                            Requires a recorded legal basis for this case. Prefer screenshots when building evidence for court.
                            Providers: {backends.join(' → ')}.
                        </p>
                        <input
                            className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm"
                            placeholder="Legal basis / authorising order reference (required)"
                            value={legalBasis}
                            onChange={(e) => setLegalBasis(e.target.value)}
                        />
                        <label className="flex items-center gap-2 text-xs text-muted-foreground">
                            How many recent posts?
                            <input
                                type="number"
                                min={1}
                                max={50}
                                value={fetchLimit}
                                onChange={(e) => setFetchLimit(Number(e.target.value))}
                                className="w-20 bg-background border border-border rounded-md px-2 py-1 text-sm"
                            />
                        </label>
                    </div>
                )}
            </section>

            {/* Step 3 — Extract */}
            <section
                ref={step3Ref}
                className={`bg-card border rounded-lg p-4 space-y-3 ${stepRing(3)}`}
                data-tour="instagram-step-extract"
            >
                <div className="flex items-center gap-2 text-sm font-medium">
                    <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-semibold">3</span>
                    Find leads
                    {activeStep === 3 && !result && (
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-pink-600 ml-1">← press the button</span>
                    )}
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex rounded-md border border-border overflow-hidden text-xs" role="group" aria-label="Save mode">
                        <button
                            type="button"
                            onClick={() => setSaveMode('preview')}
                            className={`px-3 py-2 inline-flex items-center gap-1.5 ${saveMode === 'preview' ? 'bg-secondary font-medium' : 'bg-background text-muted-foreground'}`}
                        >
                            <Eye className="w-3.5 h-3.5" /> Preview without saving
                        </button>
                        <button
                            type="button"
                            onClick={() => setSaveMode('save')}
                            className={`px-3 py-2 inline-flex items-center gap-1.5 border-l border-border ${saveMode === 'save' ? 'bg-secondary font-medium' : 'bg-background text-muted-foreground'}`}
                        >
                            <Save className="w-3.5 h-3.5" /> Save to this case
                        </button>
                    </div>
                    <button
                        type="button"
                        onClick={findLeads}
                        disabled={!canSubmit}
                        title={submitDisabledReason || 'Find leads'}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50"
                        data-tour="instagram-find-leads"
                    >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                        Find leads
                    </button>
                    {loading && (
                        <button
                            type="button"
                            onClick={cancelActiveRun}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-border text-sm hover:bg-accent"
                            title="Cancel extraction"
                        >
                            <X className="w-4 h-4" /> Cancel
                        </button>
                    )}
                    {acting && !loading && (
                        <button
                            type="button"
                            onClick={cancelActiveRun}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-border text-sm hover:bg-accent"
                            title="Cancel save and investigate"
                        >
                            <X className="w-4 h-4" /> Cancel
                        </button>
                    )}
                </div>
                <p className="text-xs text-muted-foreground">
                    {saveMode === 'preview'
                        ? 'Preview scans without writing to the case. Switch to “Save to this case” for a signed evidence record (@handle or phone/UPI/email seed).'
                        : 'Saves a signed evidence record on the case (@handle, or seed when the handle is still unknown).'}
                </p>
                {submitDisabledReason && !canSubmit && (
                    <p className="text-xs text-amber-600 flex items-center gap-1">
                        <AlertTriangle className="w-3.5 h-3.5" /> {submitDisabledReason}
                    </p>
                )}
            </section>

            {/* Past runs */}
            {caseId && (
                <section className="bg-card border border-border rounded-lg p-4 space-y-2" data-tour="instagram-run-history">
                    <div className="flex items-center gap-2 text-sm font-medium">
                        <History className="w-4 h-4" /> Past extracts for this case
                        {runsLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
                    </div>
                    {runs.length === 0 && !runsLoading ? (
                        <p className="text-xs text-muted-foreground">No saved extracts yet. Use “Save to this case” when you find leads.</p>
                    ) : (
                        <ul className="divide-y divide-border text-sm">
                            {runs.slice(0, 8).map((r) => (
                                <li key={r.id} className="py-2 flex items-center gap-2 flex-wrap">
                                    <span className="font-medium">@{r.suspect_handle || '—'}</span>
                                    <span className="text-xs text-muted-foreground">
                                        {r.artifact_count ?? 0} leads · {r.media_count ?? 0} media · {formatWhen(r.created_at)}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => reopenRun(r.id)}
                                        className="ml-auto text-xs text-primary hover:underline"
                                    >
                                        Reopen
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => pushToCanvas(r.id)}
                                        disabled={pushing}
                                        className="text-xs text-muted-foreground hover:text-foreground"
                                    >
                                        Workbench
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            )}

            {error && (
                <div className="flex items-center gap-2 text-sm text-red-500 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
                    <AlertTriangle className="w-4 h-4 shrink-0" /> {error}
                </div>
            )}
            {statusMsg && !error && (
                <div className="text-sm text-green-700 dark:text-green-400 bg-green-500/10 border border-green-500/30 rounded-md px-3 py-2">
                    {statusMsg}
                </div>
            )}

            {/* Empty state before results */}
            {!result && !loading && (
                <section className="border border-dashed border-border rounded-lg p-8 text-center space-y-2 bg-card/50">
                    <Upload className="w-8 h-8 mx-auto text-muted-foreground/50" />
                    <p className="text-sm font-medium">No leads yet</p>
                    <p className="text-xs text-muted-foreground max-w-md mx-auto">
                        Select a case, paste a bio or upload payment screenshots, then press <strong>Find leads</strong>.
                        Your results and next steps will appear here.
                    </p>
                </section>
            )}

            {/* Step 4 — Results & act */}
            {result && (
                <section
                    ref={step4Ref}
                    className={`space-y-4 ${stepRing(4)} rounded-lg p-1`}
                    data-tour="instagram-step-results"
                >                    {/* Sticky next steps */}
                    <div className="sticky top-2 z-10 bg-card border border-primary/30 shadow-sm rounded-lg p-4 space-y-3">
                        <div className="flex items-center gap-2 text-sm font-medium">
                            <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-semibold">4</span>
                            Next steps
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        </div>
                        <p className="text-xs text-muted-foreground">
                            These came from the suspect&apos;s public bio/posts. Treat as leads until confirmed elsewhere.
                            {' '}
                            {result.committed
                                ? <span className="text-green-600">Saved (run {result.run_id?.slice(0, 8)}…)</span>
                                : <span className="text-amber-600">Preview only — not on the case yet</span>}
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {!result.committed && (
                                <button
                                    type="button"
                                    onClick={() => (materialTab === 'fetch' ? runFetch(true) : runSupplied(true))}
                                    disabled={loading || !handle.trim()}
                                    className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-border hover:bg-secondary disabled:opacity-50"
                                >
                                    <Save className="w-3.5 h-3.5" /> Save to case
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={saveAndInvestigate}
                                disabled={acting || pushing || !handle.trim()}
                                className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md bg-primary text-primary-foreground disabled:opacity-50"
                                data-tour="instagram-save-investigate"
                            >
                                {(acting || pushing) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Share2 className="w-3.5 h-3.5" />}
                                Save to case & open Workbench
                            </button>
                            {result.committed && result.run_id && (
                                <button
                                    type="button"
                                    onClick={() => pushToCanvas()}
                                    disabled={pushing}
                                    className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-border hover:bg-secondary disabled:opacity-50"
                                >
                                    Open wallets on Workbench
                                </button>
                            )}
                            <Link
                                to="/instagram-network"
                                className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-border hover:bg-secondary"
                            >
                                <Network className="w-3.5 h-3.5" /> Compare IG accounts
                            </Link>
                            <button
                                type="button"
                                onClick={() => openOsintReport(result)}
                                className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-border hover:bg-secondary"
                            >
                                <FileText className="w-3.5 h-3.5" /> View printable report
                            </button>
                            {result.manifest_ndjson && (
                                <button
                                    type="button"
                                    onClick={downloadManifest}
                                    className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-border hover:bg-secondary"
                                    title="Download machine-verifiable evidence log"
                                >
                                    <Download className="w-3.5 h-3.5" /> Evidence log{result.manifest_signature ? ' (signed)' : ''}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Summary chips */}
                    <div className="bg-card border border-border rounded-lg p-4">
                        {result.warnings.includes('profile_is_private') && (
                            <div className="mb-3 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-foreground flex gap-2">
                                <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5 text-muted-foreground" />
                                <div>
                                    <div className="font-medium">Private Instagram account</div>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                        Public scrape stopped. Use lawful process (judicial order, MLAT, or the
                                        platform’s law-enforcement portal) for private content — do not try to
                                        circumvent privacy settings. Bio fields returned publicly may still appear below.
                                    </p>
                                </div>
                            </div>
                        )}
                        <div className="text-sm">
                            <span className="font-medium">
                                {result.suspect_handle?.startsWith('seed:')
                                    ? result.suspect_handle
                                    : `@${result.suspect_handle || 'no handle'}`}
                            </span>
                            <span className="text-muted-foreground">
                                {' '}· {result.media.length} media · {counts.total || 0} leads
                                {' '}({counts.valid || 0} usable)
                            </span>
                        </div>
                        <div className="flex flex-wrap gap-1.5 mt-3">
                            {Object.entries(counts)
                                .filter(([k]) => !['total', 'valid', 'rejected'].includes(k))
                                .map(([k, v]) => (
                                    <span key={k} className="text-[11px] px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground">
                                        {ARTIFACT_LABELS[k] || k}: {v}
                                    </span>
                                ))}
                        </div>
                        {result.by_surface && Object.keys(result.by_surface).length > 0 && (
                            <div className="flex flex-wrap items-center gap-1.5 mt-2">
                                <span className="text-[11px] text-muted-foreground">Found in:</span>
                                {Object.entries(result.by_surface).map(([k, v]) => (
                                    <span key={k} className={`text-[11px] px-2 py-0.5 rounded-full ${SURFACE_STYLES[k] || 'bg-secondary'}`}>
                                        {SURFACE_LABELS[k] || k}: {v}
                                    </span>
                                ))}
                            </div>
                        )}
                        {result.warnings.length > 0 && (
                            <div className="mt-2 text-xs text-amber-600">{result.warnings.join(' · ')}</div>
                        )}
                    </div>

                    <GroupedArtifactTables artifacts={result.artifacts} />

                    {/* Per-media OCR — collapsed by default */}
                    {result.media.map((m) => (
                        <details key={m.media_index} className="bg-card border border-border rounded-lg p-4">
                            <summary className="cursor-pointer text-sm font-medium flex items-center gap-2 list-none">
                                <span>{m.filename}</span>
                                <span className="text-xs text-muted-foreground font-normal">
                                    OCR {m.ocr.engine}
                                    {m.ocr.confidence != null ? ` · ${Math.round(m.ocr.confidence * 100)}%` : ''}
                                    {m.artifacts.length ? ` · ${m.artifacts.length} leads` : ''}
                                </span>
                            </summary>
                            <div className="mt-3 space-y-2">
                                {m.ocr.qr_payloads.length > 0 && (
                                    <div className="text-xs flex items-start gap-1.5">
                                        <QrCode className="w-3.5 h-3.5 mt-0.5 text-violet-500" />
                                        <div className="space-y-0.5">
                                            {m.ocr.qr_payloads.map((q, i) => (
                                                <div key={i} className="font-mono break-all">{q}</div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {m.ocr.ocr_text_original && (
                                    <pre className="whitespace-pre-wrap text-xs text-foreground/80 bg-secondary/30 rounded p-2 max-h-40 overflow-auto">
                                        {m.ocr.ocr_text_original}
                                    </pre>
                                )}
                            </div>
                        </details>
                    ))}
                </section>
            )}
        </div>
    );
};

const GroupedArtifactTables: React.FC<{ artifacts: InstagramArtifact[] }> = ({ artifacts }) => {
    const { payment, identity, other } = useMemo(() => groupArtifacts(artifacts), [artifacts]);
    if (!artifacts.length) {
        return (
            <div className="text-sm text-muted-foreground bg-card border border-border rounded-lg p-4">
                No leads found in this material. Try a clearer screenshot or paste more bio text.
            </div>
        );
    }
    return (
        <div className="space-y-3">
            <ArtifactSection title="Payment & crypto" items={payment} />
            <ArtifactSection title="Identity clues" items={identity} />
            <ArtifactSection title="Other clues" items={other} />
        </div>
    );
};

const ArtifactSection: React.FC<{ title: string; items: InstagramArtifact[] }> = ({ title, items }) => {
    if (!items.length) return null;
    const sorted = [...items].sort((a, b) => Number(b.valid) - Number(a.valid));
    return (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground bg-secondary/40 border-b border-border">
                {title} ({items.length})
            </div>
            <table className="w-full text-sm">
                <thead className="bg-secondary/30 text-xs text-muted-foreground">
                    <tr>
                        <th className="text-left px-3 py-2">Type</th>
                        <th className="text-left px-3 py-2">Value</th>
                        <th className="text-left px-3 py-2">Chain</th>
                        <th className="text-left px-3 py-2">Found in</th>
                        <th className="text-left px-3 py-2">Status</th>
                    </tr>
                </thead>
                <tbody>
                    {sorted.map((a, i) => (
                        <tr key={i} className="border-t border-border/60">
                            <td className="px-3 py-2 whitespace-nowrap">{ARTIFACT_LABELS[a.artifact_type] || a.artifact_type}</td>
                            <td className="px-3 py-2 font-mono break-all text-xs">{a.value}</td>
                            <td className="px-3 py-2">{a.chain || '—'}</td>
                            <td className="px-3 py-2">
                                <span className={`text-[11px] px-1.5 py-0.5 rounded ${SURFACE_STYLES[a.source_surface] || 'bg-secondary'}`}>
                                    {SURFACE_LABELS[a.source_surface] || a.source_surface || '—'}
                                </span>
                            </td>
                            <td className="px-3 py-2">
                                {a.valid
                                    ? <span className="text-green-600 text-xs font-medium">Usable</span>
                                    : (
                                        <span className="text-amber-600 text-xs font-medium" title={a.rejected_reason || ''}>
                                            Needs review
                                        </span>
                                    )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default InstagramOSINT;
